import React, { FC, useState } from "react";
import toast from "react-hot-toast";
import { Box, Chip, IconButton, Menu, MenuItem } from "@mui/material";
import { ReactComponent as MoreOptionsIcon } from "../../../assets/svg/more-options.svg";
import { ReactComponent as ClockIcon } from "../../../assets/svg/clock.svg";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { useUser } from "../../../hooks/useUser";
import { isAM } from "../../../utils";
import { BRAND_PROJECT_STATUSES, BRAND_PROJECT_STATUS_STYLES } from "../helper";
import ProjectStatusChip from "./ProjectStatusChip";
import { BrandProject } from "./constants";
import {
    isAlreadyRestoredError,
    restoreConfirmationCopy,
    restoreDeletedProject,
    restoreErrorMessage,
    restoredProjectMessage
} from "./projectSoftDelete";

interface Props {
    project: BrandProject;
    /** Refresh the Deleted list (the restored row leaves it). */
    onRestored: () => void;
}

const statusStyle = (status: string, prop: string): any => {
    const key = Object.keys(BRAND_PROJECT_STATUSES).find((k) => BRAND_PROJECT_STATUSES[k] === status);
    return key ? BRAND_PROJECT_STATUS_STYLES[key][prop] : null;
};

/**
 * The right-hand side of a row in the projects page's Deleted view. Stands in
 * for ProjectItemMenu: a deleted project offers none of the edit / workflow
 * actions, only Restore Project (admin / AM admin, like delete). The status
 * chip is the stored status the project was deleted at, shown read-only.
 */
export const DeletedProjectMenu: FC<Props> = ({ project, onRestored }) => {
    const { role } = useUser();
    const canRestore = isAM(role);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const status = project.status || "";

    const handleRestore = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            const result = await restoreDeletedProject(project);
            toast.success(restoredProjectMessage(result), {
                duration: result.reverted_to_scheduling ? 8000 : 4000
            });
            setSubmitting(false);
            setConfirmOpen(false);
            onRestored();
            return;
        } catch (err) {
            // OrderDeactivated (restore the order instead) carries its own message.
            toast.error(restoreErrorMessage(err));
            setSubmitting(false);
            setConfirmOpen(false);
            // Someone else restored it: the row is stale, so drop it.
            if (isAlreadyRestoredError(err)) onRestored();
        }
    };

    return (
        <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                <Chip
                    label="Deleted"
                    size="small"
                    sx={{
                        bgcolor: "var(--down-bg)",
                        color: "var(--down)",
                        fontFamily: "CircularStd",
                        fontSize: "12px",
                        fontWeight: 500,
                        borderRadius: "16px"
                    }}
                />
                {status && (
                    <Box sx={{ opacity: 0.6 }}>
                        <ProjectStatusChip
                            background={statusStyle(status, "color") || "var(--tint)"}
                            text={status}
                            textColor={statusStyle(status, "textColor") || "var(--accent-text)"}
                            icon={statusStyle(status, "icon") || <ClockIcon className="w-4 h-4" />}
                        />
                    </Box>
                )}
                {canRestore && (
                    <IconButton
                        aria-label="Deleted project actions"
                        onClick={(ev) => setAnchorEl(ev.currentTarget)}
                        sx={{ padding: "8px" }}
                    >
                        <MoreOptionsIcon />
                    </IconButton>
                )}
            </Box>

            {canRestore && (
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            setConfirmOpen(true);
                        }}
                    >
                        Restore Project
                    </MenuItem>
                </Menu>
            )}

            <ConfirmationDialog
                open={confirmOpen}
                onClose={() => {
                    if (!submitting) setConfirmOpen(false);
                }}
                onConfirm={handleRestore}
                title="Restore Project"
                description={restoreConfirmationCopy(project)}
                confirmLabel={submitting ? "Restoring..." : "Restore"}
                cancelLabel="Cancel"
                disabled={submitting}
            />
        </>
    );
};

export default DeletedProjectMenu;
